// Offline-safe backend reads: network, then this device's cached response, then the shipped GLOBAL snapshot; never throws.

import { API_BASE_URL } from "../config.js";
import { apiFetch } from "../platform/http.js";
import { fetchAsset } from "./AssetSource.js";

/** Long enough for a slow mobile link, short enough that a captive portal can't stall boot. */
const REQUEST_TIMEOUT = 8000;

const CACHE_PREFIX = "feydom_api_v1:";
/** Per entry. product-colors is the biggest real payload at ~175KB; this leaves headroom. */
const MAX_CACHE_BYTES = 512 * 1024;

const SNAPSHOT_DIR = "data/api";

function buildUrl(endpoint, params) {
  const url = `${API_BASE_URL}/api/${endpoint}/`;
  if (!params) return url;
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value != null && value !== ""),
  ).toString();
  return query ? `${url}?${query}` : url;
}

function store() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null; // Safari throws on localStorage access with cookies blocked.
  }
}

function readCache(key) {
  const ls = store();
  if (!ls) return null;
  try {
    const raw = ls.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw)?.d ?? null;
  } catch {
    return null;
  }
}

function writeCache(key, data) {
  const ls = store();
  if (!ls) return;
  let serialized;
  try {
    serialized = JSON.stringify({ t: Date.now(), d: data });
  } catch {
    return;
  }
  if (serialized.length > MAX_CACHE_BYTES) return;

  try {
    ls.setItem(CACHE_PREFIX + key, serialized);
  } catch {
    // Out of quota. The cache is disposable and the user's saved designs are not, so drop
    // every entry we own and keep only this one rather than letting the eviction pick.
    clearApiCache();
    try {
      ls.setItem(CACHE_PREFIX + key, serialized);
    } catch {
      /* still no room — the snapshot is the floor anyway */
    }
  }
}

/** Drop every cached API response. Exposed for a "clear cache" action and for tests. */
export function clearApiCache() {
  const ls = store();
  if (!ls) return;
  const keys = [];
  for (let i = 0; i < ls.length; i++) {
    const key = ls.key(i);
    if (key?.startsWith(CACHE_PREFIX)) keys.push(key);
  }
  for (const key of keys) ls.removeItem(key);
}

/**
 * A snapshot that is missing must read as a miss, not as data. A dev server answering an
 * unknown path with index.html would otherwise hand back an HTML string that every caller
 * then treats as a catalog.
 */
async function readSnapshot(candidates) {
  for (const file of candidates) {
    try {
      const response = await fetchAsset(`${SNAPSHOT_DIR}/${file}`);
      if (!response.ok) continue;
      const data = await response.json();
      if (data && typeof data === "object") return data;
    } catch {
      continue;
    }
  }
  return null;
}

/**
 * Fetch one backend endpoint, falling back to the device cache and then the shipped
 * snapshot.
 *
 * @param {string} endpoint path segment under `/api/`, e.g. `product-fabrics`
 * @param {{ params?: Record<string, string|null>,
 *           snapshot?: string|string[],
 *           cache?: boolean,
 *           timeout?: number }} [options]
 *   `snapshot` names files under `public/data/api/`; several are tried in order, which is
 *   how a per-language file falls back to the English one. `cache: false` skips the
 *   runtime cache for responses that are per-request rather than per-country.
 * @returns {Promise<any|null>} parsed JSON, or null when no layer had anything
 */
export async function fetchApiJson(endpoint, options = {}) {
  const { params = null, snapshot = null, cache = true, timeout = REQUEST_TIMEOUT } = options;
  const url = buildUrl(endpoint, params);
  const key = url.slice(API_BASE_URL.length);

  try {
    const response = await apiFetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      timeout,
      // apiFetch hands options straight to fetch on web, where `timeout` means nothing.
      signal: AbortSignal.timeout?.(timeout),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    if (cache) writeCache(key, data);
    return data;
  } catch (error) {
    console.warn(`[api] ${endpoint} unreachable (${error.message}) — falling back`);
  }

  if (cache) {
    const cached = readCache(key);
    if (cached) {
      console.info(`[api] ${endpoint}: using this device's last response`);
      return cached;
    }
  }

  const files = snapshot == null ? [] : Array.isArray(snapshot) ? snapshot : [snapshot];
  const shipped = await readSnapshot(files);
  if (shipped) {
    console.info(`[api] ${endpoint}: using the shipped snapshot`);
    return shipped;
  }

  return null;
}
